import { formatNumber } from 'utils/formatters';

import { CardConfidence, CardEvidenceRow, IntelligenceFixture } from '../../types';
import { PLACEHOLDER_CONFIDENCE_REASON, PLACEHOLDER_WHY_THIS_RESULT } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

// 589-28097 and 589-28098 print `Essential: $8.6M - 58.7%` and `Discretionary: $6.1M - 41.3%`, and
// 589-28096 states the ratio at 1.42. The pair below is the one that reproduces ALL FOUR printed
// readings: it rounds to $8.6M / $6.1M at one decimal, divides to the 58.7 / 41.3 shares, and gives
// 1.4213 -> 1.42. We shipped 1,282,000 / 714,500, which prints 64.2 / 35.8 and 1.79 — none of which
// the file states.
const ESSENTIAL_SPEND = 8628900;
const DISCRETIONARY_SPEND = 6071100;

// Rounded once, here, because three readings state this one quantity: the metric row, the ring at
// the centre of the same card and the bar the ring sits beside. Rounding again at the ring printed
// 1.421 against the row's 1.42 — `formatNumber` keeps three decimals by default.
export const essentialDiscretionaryRatio =
  Math.round((ESSENTIAL_SPEND / DISCRETIONARY_SPEND) * 100) / 100;

// `589-28092` heads its ring with the customers whose spend could be classified at all. It is a
// narrower population than `BANKED_BASE` and denominates nothing else on the band.
const CATEGORIZED_CUSTOMERS = 52400;

// The banked population every coverage reading on `589-28063` is counted against. The file never
// prints the base itself, but states it twice over and consistently: `34,272 income-detected
// customers` beside `68%` divides to exactly 50,400, and the four obligation rows of `589-28112`
// sum to 18,900, which is exactly 37.5% of it — the rate the second tile prints.
const BANKED_BASE = 50400;

// The customers carrying a cashflow model, and the denominator of both blocks down the right of the
// Income & Cashflow Health card. Also never printed, also over-determined: each FORECAST SHORTFALL
// RISK tile states a rate AND a count, and 3,464/0.084, 4,618/0.112 and 6,020/0.146 all land on
// 41,233.
const CASHFLOW_MODELLED_CUSTOMERS = 41233;

// Every derived rate on the band is rounded ONCE, here, to the single decimal the file prints. The
// bases are real divisions rather than round numbers — 1,550/18,900 is 8.2010% — and `formatNumber`
// keeps three decimals, so an unrounded rate reaches the tile as `8.201%`.
const rateOf = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;

/* PLA-8815 — the three bands `2533:1670` draws. Every constant below is a count or an amount the
   design PRINTS; each rate, ratio and total the cards state is divided out of them rather than
   listed a second time, so a band cannot report two different populations for one quantity. The
   helpers are the two shapes `rateOf` does not cover. */

// Two decimals, because the readings that use it are printed to two: `0.18`, `$28.40`.
const ratioOf = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) / 100 : 0;

const arpuOf = ratioOf;

const totalOfCounts = (rows: { value: number }[]): number =>
  rows.reduce((sum, row) => sum + row.value, 0);

/* `compact` and its steps went with the two denominators they were written for. They existed only to
   keep `1.24M eligible subscribers` on the tile in step with the panel heading that states the same
   population; with the tile line gone there is one statement of it left and nothing to keep in step.
   `SERVICE_RISK_ELIGIBLE` and `CHURN_ACTIVE_SUBSCRIBERS` stay — both still denominate their tile's
   rate and still head their panel. PLA-8815. */

// Wallet — `2570:4672`. WALLET ACTIVE USER RATE · WEEKLY TREND states all four of active, eligible,
// the previous period's rate AND its count, and they reconcile exactly: 33,242/48,600 = 68.4% and
// 31,736/48,600 = 65.3%, the two rates the panel prints. The difference is the 1,506 the card's own
// why-block names in words.
const WALLET_ACTIVE_USERS = 33242;
const WALLET_ELIGIBLE_USERS = 48600;
const WALLET_PREVIOUS_ACTIVE_USERS = 31736;
const WALLET_ACTIVE_TREND = [61.2, 62.4, 63.1, 64.8, 65.3, 66.9, 68.4];

// WALLET FEATURE ADOPTION RATE · ADOPTERS / ELIGIBLE, in the design's own order. Ordered descending,
// and the head tile reads the first entry rather than naming a feature a second time.
const WALLET_FEATURE_ADOPTION = [
  { label: 'P2P transfer', pct: 54 },
  { label: 'QR payment', pct: 44 },
  { label: 'Bill pay', pct: 31 },
  { label: 'Wallet-linked card', pct: 23 },
  { label: 'Savings pot', pct: 17 },
  { label: 'Remittance', pct: 9 }
];

// ANOMALY SEVERITY DISTRIBUTION · 1,847 USERS. The three bands sum to the 1,847 the tile reports.
const ANOMALY_SEVERITY_BANDS = [
  { label: 'Low <40', value: 1034 },
  { label: 'Medium 40–69', value: 573 },
  { label: 'High ≥70', value: 240 }
];

// Insurance — `2561:4246`. POLICY STATUS DISTRIBUTION sums to 83,150, which is the `Eligible
// policies` the ring beside it states, and 72,840/83,150 is the 87.6% at its centre.
const POLICIES_ACTIVE = 72840;
const POLICY_STATUS_COUNTS = [
  { label: 'Active', value: POLICIES_ACTIVE },
  { label: 'Lapsed', value: 4490 },
  { label: 'Cancelled / terminated', value: 3243 },
  { label: 'Surrendered / other', value: 2577 }
];
const CLAIM_COUNT = 13111;

// RENEWAL RISK · 18,400 RENEWAL-DUE POLICIES. The bands sum to the 18,400 in the heading, and the
// high band is both the `High-risk renewal policies` row and the numerator of the 18.7% tile.
const RENEWAL_RISK_HIGH = 3441;
const RENEWAL_RISK_BANDS = [
  { label: 'Low <40', value: 9568 },
  { label: 'Medium 40–69', value: 5391 },
  { label: 'High ≥70', value: RENEWAL_RISK_HIGH }
];

// RIDER / CROSS-SELL READINESS BANDS `2561:4553`. Module-level for the same reason the two arrays
// above are: the Ready band is BOTH the first segment of the bar and the numerator of the 27.8% tile
// beside it, so one array has to serve both or they drift. Read at 348% — `Suppressed` is 4,672, and
// the five total 58,388, which is what makes 16,230/58,388 land on the printed 27.8%.
const RIDER_READY = 16230;
const RIDER_READINESS_BANDS = [
  { label: 'Ready', value: RIDER_READY },
  { label: 'Potential', value: 19966 },
  { label: 'Not ready', value: 14600 },
  { label: 'Suppressed', value: 4672 },
  { label: 'Insufficient', value: 2920 }
];

// Telco — `2558:3743`. Both distributions are headed with the population they cover, and both
// high bands are also the tile beside them: 208,320/1,239,320 = 16.8%, 142,600/1,150,000 = 12.4%.
const SERVICE_RISK_HIGH = 208320;
const SERVICE_RISK_ELIGIBLE = 1239320;
const CHURN_RISK_HIGH = 142600;
const CHURN_ACTIVE_SUBSCRIBERS = 1150000;
const SERVICE_RISK_RATE_MOVEMENT = 1.4;
const SERVICE_RISK_COUNT_MOVEMENT = 5530;
const CHURN_RISK_PREVIOUS_RATE = 13.3;
const CHURN_RISK_COUNT_MOVEMENT = 1530;

/* The four Churn tiles are the one tile variant on the three new bands that carries a chart:
   `2558:3744` draws the 142x56 marked line inside each of them — the widget `589-29285` already
   draws on Risk & Protection's High-risk Customers tile, which is the chart the FR names by
   pointing at it. The file draws a VECTOR, not a series, so — exactly as `riskProtection.ts` ships
   one — the readings between the ends are fixture stand-ins for the trend the seam will carry.

   What is NOT a stand-in is either END. Every series starts at the tile's own previous period and
   finishes at its current value, so a tile and the chart inside it can never state two different
   movements. `telco_churn_risk_rate` is over-determined and lands where the file's own right panel
   puts it: `Previous churn-risk rate 13.3%` against the 12.4% the tile reads.

   The interior weights are one normalised shape shared by all four, so no series claims a turning
   point of its own that nothing states. Seven readings, the length the two panel trends beside them
   already use; the widget itself assumes no count. */
const TILE_TREND_SHAPE = [0, 0.14, 0.33, 0.27, 0.55, 0.78, 1];

// The interpolation is rounded to the ENDS' own precision: a count reading is what the marker's
// readout prints, and a whole-number series interpolated at two decimals reveals `203,564.2
// subscribers` under the pointer.
const trendBetween = (previous: number, current: number): number[] => {
  const whole = Number.isInteger(previous) && Number.isInteger(current);

  return TILE_TREND_SHAPE.map(weight => {
    const reading = previous + (current - previous) * weight;

    return whole ? Math.round(reading) : Math.round(reading * 100) / 100;
  });
};

// ARPU MOVEMENT · DAILY TREND prints the division: $32.66M / 1.15M = $28.40, against a $27.30
// baseline — the `+$1.10 / +4.0%` the same panel states.
const TELCO_BILLABLE_REVENUE = 32660000;
const TELCO_ACTIVE_SUBSCRIBERS = CHURN_ACTIVE_SUBSCRIBERS;
const TELCO_BASELINE_ARPU = 27.3;
const TELCO_REVENUE_TREND = [30.9, 31.2, 31.4, 31.9, 32.1, 32.4, 32.66];

// ALLOWANCE UTILIZATION DISTRIBUTION states each allowance as used-over-included, and both divide
// to the rate its tile prints: 8.45/11.0 = 76.8%, 1.92/4.41 = 43.5%.
const TELCO_DATA_USED_PB = 8.45;
const TELCO_DATA_INCLUDED_PB = 11.0;
const TELCO_VOICE_USED_MIN = 1.92;
const TELCO_VOICE_INCLUDED_MIN = 4.41;

/* PLA-8815 — the readings the six panels `2533:1670` draws print beside their charts. Every one is
   a figure the design STATES; the shares and quotients the panels also print are divided out of
   them below rather than listed a second time. */

// The component scores each composite ring breaks itself into, in the design's own order. `Missed
// expected recharge` is NEGATIVE in the file (-9) — a penalty inside the composite, not a score —
// so the shape carries a signed value rather than assuming a 0-100 reading.
const DIVERSITY_COMPONENTS = [
  { label: 'Merchant categories', value: 0.76 },
  { label: 'Payment types', value: 0.71 },
  { label: 'Channels', value: 0.69 },
  { label: 'Previous period', value: 0.66 }
];

const TOPUP_COMPONENTS = [
  { label: 'Count vs baseline', value: 79 },
  { label: 'Interval consistency', value: 76 },
  { label: 'Amount stability', value: 68 },
  { label: 'Recency', value: 73 }
];

const PREMIUM_HEALTH_COMPONENTS = [
  { label: 'On-time payment', value: 89 },
  { label: 'Days-past-due inverse', value: 78 },
  { label: 'Failed-payment inverse', value: 76 },
  { label: 'Auto-debit success', value: 86 }
];

const RECHARGE_COMPONENTS = [
  { label: 'Frequency consistency', value: 78 },
  { label: 'Amount stability', value: 71 },
  { label: 'Recency inverse', value: 76 },
  { label: 'Missed expected recharge', value: -9 }
];

// ANOMALY SIGNAL EVIDENCE — four chips, each a signal NAME over the test that raised it and the
// share of anomalies it accounts for.
const ANOMALY_SIGNALS = [
  { label: 'Amount', test: 'Z-score >3', value: 38 },
  { label: 'Count', test: 'Velocity spike', value: 29 },
  { label: 'Device', test: 'New/risky', value: 19 },
  { label: 'Channel', test: 'Unusual mix', value: 14 }
];

// CLAIMS FREQUENCY & RATIO prints both divisions: 13,111 / 72,840 = 0.18 and $28.4M / $64.5M = 44.0%.
const CLAIM_AMOUNT = 28400000;
const PREMIUM_AMOUNT = 64500000;

// RENEWAL RISK · 18,400 RENEWAL-DUE POLICIES, and the previous-period rate the panel states beneath.
const RENEWAL_DUE_POLICIES = 18400;
const RENEWAL_PREVIOUS_RATE = 20.8;

// The two Insurance rings state their own previous period, which no other reading on the card gives.
const POLICY_ACTIVE_PREVIOUS_RATE = 85.8;

// SERVICE-RISK EVIDENCE closes on a freshness row; the churn panel closes on a movement row.
const NETWORK_EVENT_FRESHNESS_MINUTES = 18;
const CHURN_HIGH_RISK_MOVEMENT = -10350;

// Medians the two composite panels print beside their rings.
const WALLET_MEDIAN_TOPUPS_PER_MONTH = 4.2;
const WALLET_MEDIAN_TOPUP_AMOUNT = 86;
const RECHARGE_MEDIAN_PER_MONTH = 3.8;
const RECHARGE_MEDIAN_AMOUNT = 18.5;

// PLAN UPGRADE READINESS BANDS. `Ready` over the five bands is the 24.6% the tile prints.
const UPGRADE_READY = 283000;
const UPGRADE_READINESS_BANDS = [
  { label: 'Ready', value: UPGRADE_READY },
  { label: 'Potential', value: 396000 },
  { label: 'Not ready', value: 311000 },
  { label: 'Suppressed', value: 92000 },
  { label: 'Insufficient', value: 69000 }
];
const RECHARGE_REGULARITY_SCORE = 73;
const RECHARGE_PREVIOUS_SCORE = 68;

// The ring and the three lines beside it in INCOME COVERAGE & STABILITY. `detected` is printed; the
// 68% at the ring's centre and the coverage tile above it are both taken against `BANKED_BASE`.
// The band word beside each of the two lines is stated by the file, not derived from the figure: no
// threshold on this page maps 84 and 76 onto the same word, so reading it off a cutoff here would be
// this module's invention rather than the payload's claim.
export const bankingIncomeCoverage: {
  detected: number;
  base: number;
  resultConfidence: number;
  resultConfidenceBand: CardConfidence['band'];
  stabilityScore: number;
  stabilityBand: CardConfidence['band'];
} = {
  detected: 34272,
  base: BANKED_BASE,
  resultConfidence: 84,
  resultConfidenceBand: 'high',
  stabilityScore: 76,
  stabilityBand: 'high'
};

// CASHFLOW HEALTH. Counts, not the shares the file prints: 48 / 34 / 18 are what these divide to
// against `CASHFLOW_MODELLED_CUSTOMERS`, and the section takes the share so a payload that reports
// four bands — or reports counts against a different base — still draws itself correctly.
export const bankingCashflowHealth = [
  { label: 'Healthy positive cashflow', value: 19792 },
  { label: 'Stable / watch', value: 14019 },
  { label: 'Negative / stressed', value: 7422 }
];

// FORECAST SHORTFALL RISK. Each tile states a horizon, a rate and a customer count; only the count
// is held here, because the rate is that count against the modelled base and the file's three rates
// all reproduce from it. Ordered widest-last — the headline tile reads the final entry.
export const bankingForecastShortfallRisk = [
  { label: 'Next 7 days', customers: 3464 },
  { label: 'Next 14 days', customers: 4618 },
  { label: 'Next 30 days', customers: 6020 }
].map(horizon => ({
  ...horizon,
  base: CASHFLOW_MODELLED_CUSTOMERS,
  rate: rateOf(horizon.customers, CASHFLOW_MODELLED_CUSTOMERS)
}));

// `589-28112`, read from the file itself. Spelling is the file's own, including `Credit-card`
// hyphenated where the two loan rows space their slashes.
// The six bars are NOT one scale, which is why the counts are split into two groups here rather than
// listed flat with a share each. Measured against the panel's own track, the four obligation bars
// run 0.42 / 0.31 / 0.19 / 0.08 — each row against the 18,900 the four of them sum to — while the
// two arrears bars run 0.65 / 0.36, each against their own 1,550. One shared denominator reproduces
// neither group, which is what made an earlier pass ship the panel with no bars at all.
const DEBT_OBLIGATION_COUNTS = [
  { label: 'Mortgage / home loan', count: 7938 },
  { label: 'Personal / instalment loan', count: 5859 },
  { label: 'Credit-card repayment', count: 3591 },
  { label: 'Other recurring obligation', count: 1512 }
];

const ARREARS_COUNTS = [
  { label: 'Missed / late repayment', count: 992 },
  { label: 'Payment failure', count: 558 }
];

const sumOf = (rows: { count: number }[]): number =>
  rows.reduce((total, { count }) => total + count, 0);

const DEBT_OBLIGATION_TOTAL = sumOf(DEBT_OBLIGATION_COUNTS);
const ARREARS_TOTAL = sumOf(ARREARS_COUNTS);

// Share is taken inside the group, so a payload that adds a seventh row — or drops one — rescales
// only its own group's bars. A group that arrives empty divides nothing: the rows are gone with it.
const evidenceRowsFor = (rows: { label: string; count: number }[]): CardEvidenceRow[] => {
  const total = sumOf(rows);

  return rows.map(({ label, count }) => ({
    label,
    value: formatNumber(count),
    share: total > 0 ? count / total : undefined
  }));
};

const DEBT_ARREARS_EVIDENCE_ROWS = [
  ...evidenceRowsFor(DEBT_OBLIGATION_COUNTS),
  ...evidenceRowsFor(ARREARS_COUNTS)
];

// Both rates the card's 2nd and 3rd tiles print, derived from the rows the evidence panel below them
// draws so the tile and the panel cannot state two different populations. Arrears is read against
// the customers who carry an obligation at all, NOT against the banked base: 1,550/18,900 is the
// 8.2% the file prints, where 1,550/50,400 would print 3.1%.
const DEBT_OBLIGATION_SIGNAL_RATE = rateOf(DEBT_OBLIGATION_TOTAL, BANKED_BASE);
const ARREARS_SIGNAL_RATE = rateOf(ARREARS_TOTAL, DEBT_OBLIGATION_TOTAL);

export const industryCardFixtures: Record<string, IntelligenceFixture> = {
  // Card order follows the file: `589-28063` puts Debt & Spending Health at Left 330.8 and
  // Income & Cashflow Health at Left 1000.8. The registry, not this map, decides render sequence.
  debt_spending_health: {
    cardKey: 'debt_spending_health',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // `SPEND COMPOSITION` (`589-28092`) is the block that carries the two `View Details` links, and
    // it is on THIS card — the key sat on the sibling, which draws no spend block at all.
    breakdownKey: 'essential_vs_discretionary',
    metrics: [
      {
        key: 'essential_discretionary_ratio',
        label: 'Essential vs Discretionary Spend Ratio',
        // Derived from the same 58.7 / 41.3 split `breakdownFixtures.essential_vs_discretionary`
        // already publishes for this quantity, so the card body and the breakdown behind it cannot
        // state two different splits.
        value: essentialDiscretionaryRatio,
        format: 'ratio',
        denominator: 'essential spend per unit of discretionary spend',
        // Drawn in the file's POSITIVE ink, unlike the two tiles beside it. A rising essential share
        // is the reading a bank acts on rather than one it is alarmed by, and the file is consistent
        // about it: this is the one green delta on the band.
        delta: { value: 0.08, unit: 'ratio', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'debt_obligation_signal_rate',
        label: 'Debt Obligation Signal Rate',
        value: DEBT_OBLIGATION_SIGNAL_RATE,
        format: 'percent',
        denominator: 'of the banked base carries a recurring obligation',
        // Falling, and still drawn in the file's negative ink on both this tile and the arrears one.
        // Direction and sentiment are separate fields precisely so a fall can read as bad news.
        delta: { value: 1.1, unit: 'pp', direction: 'down', sentiment: 'negative' }
      },
      {
        key: 'arrears_signal_rate',
        label: 'Arrears Signal Rate',
        value: ARREARS_SIGNAL_RATE,
        format: 'percent',
        denominator: 'of customers with an obligation missed a repayment',
        delta: { value: 1.1, unit: 'pp', direction: 'down', sentiment: 'negative' }
      }
    ],
    evidence: {
      label: 'DEBT & ARREARS EVIDENCE',
      rows: DEBT_ARREARS_EVIDENCE_ROWS
    },
    // `High confidence · 82%` on both cards of the band, and the `Reason:` line beside each chip is
    // still the short Lorem the file ships.
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    // `589-28146` is still Lorem Ipsum, so the box ships and the copy stays a placeholder.
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT
  },
  income_cashflow_health: {
    cardKey: 'income_cashflow_health',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'income_detection_coverage',
        label: 'Salary / Income Detection Coverage',
        value: (bankingIncomeCoverage.detected / BANKED_BASE) * 100,
        format: 'percent',
        denominator: 'of the banked base has a detectable income credit',
        delta: { value: 5, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'income_stability_score',
        label: 'Income Stability Score',
        value: bankingIncomeCoverage.stabilityScore,
        format: 'points',
        denominator: 'across customers with a detected income',
        delta: { value: 5, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        // PLA-8828. NOT a Figma read: `589-28063` draws three tiles on this card and O-BFSI-003 is
        // the catalogue's third of four, so the file has no tile to read. The reading is what the
        // metric answered on UAT `generic` 2026-09-18 -- `calculation_status: not_applicable`,
        // `reason_code: industry_out_of_scope` for a tenant outside Banking/BFSI, which the live
        // parse carries as a WITHHELD value. `null` here rather than an invented score, for the
        // reason the whole of PLA-8815 emptied these files: a figure nothing measured reads exactly
        // like one that was. No `delta` for the same reason -- no response carries a previous period.
        key: 'cashflow_health_score',
        label: 'Cashflow Health Score',
        value: null,
        format: 'points',
        denominator: 'across customers with a detected income and balance history'
      },
      {
        key: 'cashflow_forecast_risk_rate',
        label: 'Cashflow Forecast Risk Rate',
        // The widest of the three horizons the FORECAST SHORTFALL RISK block draws, which is the one
        // the tile prints. Reading it off the block rather than restating it keeps the headline and
        // the last tile below it from disagreeing.
        value: bankingForecastShortfallRisk[bankingForecastShortfallRisk.length - 1].rate,
        format: 'percent',
        denominator: 'of modelled customers face a 30-day shortfall',
        // A falling risk RATE is the good direction, so the fall is `positive` (registry
        // `riseMeans: 'negative'` — a rise in forecast shortfall risk is the bad direction).
        delta: { value: 1.1, unit: 'pp', direction: 'down', sentiment: 'positive' }
      }
    ],
    // No `evidence` panel on this card: what it draws in that place is the cashflow and forecast
    // blocks, which are visuals rather than a rows-and-shares payload. The `EVIDENCE INPUTS` chip
    // row (`589-28229`) is card visuals on both, so it lives in `industryEvidenceInputs`.
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    // `589-28228` is still Lorem Ipsum — see `debt_spending_health` above.
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT
  },
  /* MOCKUP EXISTS NOW. The seven fixtures below used to carry invented figures because page 1 drew
     Banking only. `2533:1670` draws all three remaining bands, so every value below is READ from it
     rather than composed: `2570:4672` (Wallet), `2561:4246` (Insurance), `2558:3743` (Telco), all
     transcribed at verified 100% zoom.

     Where the design states a figure twice, the pair is reproduced from the counts rather than
     restated, so tile and panel cannot disagree — and every one of those pairs reconciles exactly:
     33,242/48,600 = 68.4%, 72,840/83,150 = 87.6%, 13,111/72,840 = 0.18, 3,441/18,400 = 18.7%,
     142,600/1.15M = 12.4%, $32.66M/1.15M = $28.40, 8.45/11.0 PB = 76.8%, 283K/1,151K = 24.6%,
     and the anomaly severity bands 1,034+573+240 sum to the 1,847 flagged users the tile reports.

     TWO TILES CONTRADICT THEIR OWN CARD, and the rule applied to both is the same: prefer the
     reading that other stated numbers DETERMINE over one that nothing supports.
       - Recharge Regularity: the tile prints `▼ 7 pts`, but the panel states `Previous score 68`
         against a value of 73, and the card's own why-block says regularity improved. 68 -> 73 is
         +5, determined twice; the -7 is determined by nothing. Encoded +5 up.
       - Plan Upgrade Readiness: the tile prints `▼ 3.4 pp` while the why-block says readiness
         increased. Here nothing states a previous rate, so the tile is the only reading there is
         and it stands as drawn.
     Both are reported to the designer.

     `confidence.reasons` stay ours: every `Reason:` line in the design is Lorem Ipsum placeholder,
     so there is no copy to extract. The SCORES and BANDS are the design's own. */
  /* None of these four tiles carries a denominator. `of 1.24M eligible subscribers`, `in the high
     service-risk band`, `of 1.15M active subscribers` and `in the high churn-risk band` were removed
     on the user's instruction of 2026-08-20.

     No reading is lost with them, which is the thing to check before deleting a base: both
     populations are still stated on the card by the panel headings the section draws from the same
     constants — `SERVICE-EXPERIENCE RISK · 1.24M ELIGIBLE SUBSCRIBERS` and `TELCO CHURN-RISK
     DISTRIBUTION · 1.15M ACTIVE SUBSCRIBERS` — and both counts are stated a second time by the
     `High ≥70` row of each distribution's legend, which is 208,320 and 142,600. */
  churn_service_experience: {
    cardKey: 'churn_service_experience',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'service_experience_risk_rate',
        label: 'Network / Service Experience Risk Rate',
        value: rateOf(SERVICE_RISK_HIGH, SERVICE_RISK_ELIGIBLE),
        format: 'percent',
        delta: {
          value: SERVICE_RISK_RATE_MOVEMENT,
          unit: 'pp',
          direction: 'up',
          sentiment: 'negative'
        },
        sparkline: trendBetween(
          rateOf(SERVICE_RISK_HIGH, SERVICE_RISK_ELIGIBLE) - SERVICE_RISK_RATE_MOVEMENT,
          rateOf(SERVICE_RISK_HIGH, SERVICE_RISK_ELIGIBLE)
        )
      },
      {
        key: 'subscribers_with_service_risk',
        label: 'Subscribers with Service Risk',
        value: SERVICE_RISK_HIGH,
        format: 'count',
        delta: {
          value: SERVICE_RISK_COUNT_MOVEMENT,
          unit: 'count',
          direction: 'up',
          sentiment: 'negative',
          secondary: { value: 9.4, unit: 'pp' }
        },
        sparkline: trendBetween(SERVICE_RISK_HIGH - SERVICE_RISK_COUNT_MOVEMENT, SERVICE_RISK_HIGH)
      },
      {
        key: 'telco_churn_risk_rate',
        label: 'Telco Churn Risk Rate',
        value: rateOf(CHURN_RISK_HIGH, CHURN_ACTIVE_SUBSCRIBERS),
        format: 'percent',
        // The file states this movement twice: the tile's `▼ 0.9 pp` against 12.4%, and the right
        // panel's own `Previous churn-risk rate 13.3%`. Divided out of the pair rather than
        // restated, so the tile, the panel row and the chart cannot drift apart.
        delta: {
          value:
            Math.round(
              (CHURN_RISK_PREVIOUS_RATE - rateOf(CHURN_RISK_HIGH, CHURN_ACTIVE_SUBSCRIBERS)) * 10
            ) / 10,
          unit: 'pp',
          direction: 'down',
          sentiment: 'positive'
        },
        sparkline: trendBetween(
          CHURN_RISK_PREVIOUS_RATE,
          rateOf(CHURN_RISK_HIGH, CHURN_ACTIVE_SUBSCRIBERS)
        )
      },
      {
        key: 'high_risk_active_subscribers',
        label: 'High-risk Active Subscribers',
        value: CHURN_RISK_HIGH,
        format: 'count',
        delta: {
          value: CHURN_RISK_COUNT_MOVEMENT,
          unit: 'count',
          direction: 'down',
          sentiment: 'positive',
          secondary: { value: 2.4, unit: 'pp' }
        },
        sparkline: trendBetween(CHURN_RISK_HIGH + CHURN_RISK_COUNT_MOVEMENT, CHURN_RISK_HIGH)
      }
    ],
    confidence: {
      score: 55,
      band: 'medium',
      reasons: [
        'Complaint records matched to a customer for 88% of contacts',
        'Network quality reports available for the full period'
      ]
    },
    // The file draws TWO why-blocks per card, each headed with its own question. The answers are the
    // design's own, verbatim; the questions are copy and live with the block headings.
    whyThisResult:
      'Network-quality incidents declined in two high-volume regions and repeat complaints fell 11% after resolution.',
    evidenceNote:
      'Recharge gaps narrowed and data usage stabilized, moving 10,350 subscribers out of the high-risk band.'
  },
  plan_usage_revenue: {
    cardKey: 'plan_usage_revenue',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'current_arpu',
        label: 'Current ARPU',
        value: arpuOf(TELCO_BILLABLE_REVENUE, TELCO_ACTIVE_SUBSCRIBERS),
        format: 'currency',
        denominator: 'billable revenue per active subscriber',
        // The card's own ARPU MOVEMENT panel states baseline $27.30 and `+$1.10 / +4.0%`, which is
        // exactly this value less the baseline. The tile itself prints `▲ $800 (6.2% pp)` against a
        // $28.40 reading, which is not a movement of this quantity at all -- raised with the
        // designer, and the panel's coherent pair used until it is answered.
        delta: {
          value: arpuOf(TELCO_BILLABLE_REVENUE, TELCO_ACTIVE_SUBSCRIBERS) - TELCO_BASELINE_ARPU,
          unit: 'currency',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 4.0, unit: 'percent' }
        }
      },
      {
        key: 'total_billable_revenue',
        label: 'Total Billable Revenue',
        value: TELCO_BILLABLE_REVENUE,
        format: 'currency',
        denominator: 'billed across the period'
        // No delta: the tile prints `▲ $120 (7.6% pp)` against $32.66M, which no other reading on
        // the card supports, and nothing states a previous revenue to derive one from. An absent
        // delta is a tile that says nothing; an invented one is a tile that says something false.
        //
        // No `sparkline` either. `2558:3932` draws NO chart inside this tile — the daily revenue
        // trend is the card's LEFT panel, `ARPU MOVEMENT · DAILY TREND` — and the series shipped
        // here was in the panel's own unit: readings of 30.9..32.66 (millions) against a
        // 32,660,000 value, which the tile's hover readout printed as `$31`. It is parked on
        // `industryVisuals.telco.arpuTrend` for the panel that reads it.
      },
      {
        key: 'data_utilisation_rate',
        label: 'Data Utilization Rate',
        value: rateOf(TELCO_DATA_USED_PB, TELCO_DATA_INCLUDED_PB),
        format: 'percent',
        denominator: `${TELCO_DATA_USED_PB.toFixed(2)} PB of ${TELCO_DATA_INCLUDED_PB.toFixed(
          1
        )} PB included`,
        delta: { value: 3.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'voice_utilisation_rate',
        label: 'Voice Utilization Rate',
        value: rateOf(TELCO_VOICE_USED_MIN, TELCO_VOICE_INCLUDED_MIN),
        format: 'percent',
        denominator: `${TELCO_VOICE_USED_MIN.toFixed(2)}B of ${TELCO_VOICE_INCLUDED_MIN.toFixed(
          2
        )}B minutes included`,
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' }
      }
    ],
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Plan and usage records reconciled for every active customer',
        'Revenue read from billed amounts rather than from list price'
      ]
    },
    whyThisResult:
      'Higher data add-on and roaming revenue outweighed a small decline in voice usage.',
    evidenceNote:
      'Evening streaming and social usage increased, while included plan allowances remained broadly unchanged.'
  },
  /* None of these four tiles carries a denominator. `2558:3932` draws them as label over figure and
     nothing else, and the four sentences that stood here — `composite of frequency, amount and
     recency`, `recharging on a consistent interval`, `of evaluated subscribers ready to upgrade`,
     `clear every handoff guardrail` — were this fixture's gloss rather than the file's copy. They
     were also what made the strip wrap two-and-two instead of running four across. PLA-8815. */
  recharge_upgrade_readiness: {
    cardKey: 'recharge_upgrade_readiness',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'recharge_regularity_score',
        label: 'Recharge Regularity Score',
        value: RECHARGE_REGULARITY_SCORE,
        format: 'points',
        // See the header note: the tile's `▼ 7 pts` is contradicted by the panel's own
        // `Previous score 68` and by the card's why-block. 68 -> 73 is the determined reading.
        delta: {
          value: RECHARGE_REGULARITY_SCORE - RECHARGE_PREVIOUS_SCORE,
          unit: 'points',
          direction: 'up',
          sentiment: 'positive'
        }
      },
      {
        key: 'regular_recharge_subscribers',
        label: 'Regular Recharge Subscribers',
        value: 486300,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 9.4, unit: 'pp' }
        }
      },
      {
        key: 'plan_upgrade_readiness_rate',
        label: 'Plan Upgrade Readiness Rate',
        value: rateOf(UPGRADE_READY, totalOfCounts(UPGRADE_READINESS_BANDS)),
        format: 'percent',
        // Left as the tile draws it: unlike the score above, nothing on this card states a previous
        // rate, so there is no determined reading to prefer over it -- even though the why-block
        // beside it says readiness increased. Both halves reported to the designer.
        delta: { value: 3.4, unit: 'pp', direction: 'down', sentiment: 'negative' }
      },
      {
        key: 'upgrade_growth_planner_eligible',
        label: 'Growth Planner Eligible',
        value: 238900,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 9.4, unit: 'pp' }
        }
      }
    ],
    confidence: {
      score: 35,
      band: 'low',
      reasons: [
        'Recharge history complete for the selected period',
        'Readiness leans on usage headroom, which lags a sudden change of plan'
      ]
    },
    whyThisResult:
      'Recharge intervals became more consistent and missed expected recharges declined 9%.',
    evidenceNote:
      'More subscribers exceeded 80% data utilization and purchased add-ons while service-risk guardrails remained acceptable.'
  },
  /* None of these four tiles carries a denominator. `2561:4246` draws them as label over figure over
     delta and nothing else, and the four sentences that stood here — `of 83,150 eligible policies`,
     `in good standing at period end`, `composite of on-time, arrears and auto-debit signals` and
     `13,111 claims over 72,840 active policies` — were this fixture's gloss rather than the file's
     copy. The geometry settles it: `div.kpi` `2584:1708` is Hug 87px = 12 padding + `Frame 40509`
     63px + 12 padding, and `Frame 40509` holds exactly TWO children, the label Text and the frame
     carrying value + delta. There is no third node and no room for one.

     Every base they stated is still on the card, so nothing became unreadable: `83,150` is the
     `Eligible policies` row beside the ring, the good-standing test is the `Good-standing checks`
     row, the composite is spelled out by the four PREMIUM PAYMENT HEALTH components, and both claim
     figures are printed in full by CLAIMS FREQUENCY & RATIO as `13,111 ÷ 72,840 = 0.18`. PLA-8815. */
  policy_health_claims: {
    cardKey: 'policy_health_claims',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'policy_active_rate',
        label: 'Policy Active Rate',
        value: rateOf(POLICIES_ACTIVE, totalOfCounts(POLICY_STATUS_COUNTS)),
        format: 'percent',
        delta: { value: 1.8, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'active_policies',
        label: 'Active Policies',
        value: POLICIES_ACTIVE,
        format: 'count',
        delta: {
          value: 3200,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 2.1, unit: 'pp' }
        }
      },
      {
        key: 'premium_payment_health',
        label: 'Premium Payment Health',
        value: 82,
        format: 'points',
        delta: { value: 3.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'claims_frequency',
        label: 'Claims Frequency',
        // The card prints the division itself: `13,111 ÷ 72,840 = 0.18`. Taken here rather than
        // restated so the tile and that line cannot drift apart.
        value: ratioOf(CLAIM_COUNT, POLICIES_ACTIVE),
        format: 'ratio',
        delta: { value: 3.1, unit: 'pp', direction: 'down', sentiment: 'positive' }
      }
    ],
    // No `evidence` panel. `2561:4246` draws no rows-and-shares block on this card at all — what it
    // puts under the claims tile is CLAIMS FREQUENCY & RATIO, four reading rows the section now
    // draws, and the three-row settled/open/declined split shipped here was composed when the file
    // had no mockup for the band. A panel the design does not draw is a reading nobody can check.
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Premium and claim amounts reconciled to the policy ledger',
        'Claims still open are valued at reserve, not at settlement'
      ]
    },
    whyThisResult:
      'Premium recovery and reinstatement activity moved 1,580 policies back into good standing while new lapses declined.',
    // `themain` in the file is the file's own typo, not a word; shipped as two words. Everything
    // else is verbatim.
    evidenceNote:
      'On-time and auto-debit success are strong; days past due and failed-payment history remain the main negative factors.'
  },
  /* Same as the card above: `2561:4246` gives these four tiles a label, a figure and a delta, and no
     denominator line. `of 18,400 renewal-due policies`, `of eligible policyholders carry a gap
     signal`, `of evaluated policyholders are rider-ready` and `clear every handoff guardrail` were
     ours, on a tile the file draws at the same Hug 87px with the same two-child inner frame.

     The first was also a SECOND source of truth for 18,400, which `RENEWAL_DUE_POLICIES` already
     holds and the RENEWAL RISK heading already prints. `coverage_gap_signal_rate` is now the one
     rate on the band with no base stated anywhere on the card — deliberate, because the file states
     none either: `2561:4522` heads its panel `COVERAGE GAP SIGNALS · ELIGIBLE POLICYHOLDERS` with no
     count. Reported to the user rather than invented back. PLA-8815. */
  renewal_growth_opportunities: {
    cardKey: 'renewal_growth_opportunities',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'renewal_risk_rate',
        label: 'Renewal Risk Rate',
        value: rateOf(RENEWAL_RISK_HIGH, totalOfCounts(RENEWAL_RISK_BANDS)),
        format: 'percent',
        delta: { value: 2.1, unit: 'pp', direction: 'up', sentiment: 'negative' }
      },
      {
        key: 'coverage_gap_signal_rate',
        label: 'Coverage Gap Signal Rate',
        value: 31.4,
        format: 'percent',
        delta: { value: 3.6, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'rider_cross_sell_readiness',
        label: 'Rider / Cross-sell Readiness',
        // Divided out of the readiness bands like every other rate on the band. It was carried as a
        // literal 27.8 under a note saying the design stated it rather than derived it, because the
        // bands divided to 27.70 — but that gap was a mistyped input, not a design decision:
        // `Suppressed` is 4,672 in `2561:4553`, not the 4,872 shipped here. At 4,672 the five bands
        // total 58,388 and 16,230/58,388 rounds to exactly the 27.8% the tile prints.
        value: rateOf(RIDER_READY, totalOfCounts(RIDER_READINESS_BANDS)),
        format: 'percent',
        delta: { value: 2.9, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'growth_planner_eligible',
        label: 'Growth Planner Eligible',
        value: 14620,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'down',
          sentiment: 'negative',
          secondary: { value: 5.4, unit: 'pp' }
        }
      }
    ],
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Renewal dates held for every active policy',
        'Cover gaps inferred from the product mix a policyholder already holds'
      ]
    },
    whyThisResult:
      'Premium-payment recovery improved and complaint resolution reduced high-risk renewal cases by 387 policies.',
    evidenceNote:
      'New life-stage and protection-gap evidence identified more eligible policyholders, while product and consent guardrails remained stable.'
  },
  wallet_activity_funding: {
    cardKey: 'wallet_activity_funding',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'wallet_active_user_rate',
        label: 'Wallet Active User Rate',
        value: rateOf(WALLET_ACTIVE_USERS, WALLET_ELIGIBLE_USERS),
        format: 'percent',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'active_eligible_users',
        label: 'Active / Eligible Users',
        value: WALLET_ACTIVE_USERS,
        format: 'count',
        // The card's why-block states the same movement in words -- "adding 1,506 distinct active
        // users" -- and 33,242 less the previous period's 31,736 is exactly that. The tile's own
        // `▲ 3,200 (+8.1% pp)` is the placeholder pair repeated across several tiles on this board;
        // the determined figure is used instead. Reported to the designer.
        delta: {
          value: WALLET_ACTIVE_USERS - WALLET_PREVIOUS_ACTIVE_USERS,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive'
        }
        // No `sparkline`: `2570:4672` draws no chart inside this tile either, and the series that
        // shipped here was the RATE trend of the card's left panel — readings of 61.2..68.4 against
        // a 33,242 count. Parked on `industryVisuals.fintech.walletActiveRateTrend`.
      },
      {
        key: 'topup_frequency_score',
        label: 'Top-up Frequency Score',
        value: 74,
        format: 'points',
        delta: { value: 6, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'highest_feature_adoption',
        label: 'Highest Feature Adoption',
        /* The ONLY tile denominator left on this band, and it survives deliberately. `2570:4672`
           draws no sub-line on any tile -- `div.kpi` is Hug 87 = padding 12 + `Frame 40509` 63 +
           padding 12, and that frame holds the label and the value/delta pair and nothing else --
           so the other seven were removed. This one stays because the file folds the feature INTO
           the figure (`2584:1814` reads `P2P . 54%`) and the fixture cannot say that: `valueText`
           is the only route and it forces `value: null`, which trips `hasValue` in
           `StatBox.tsx:242` and silently drops the `delta` that `:315` gates on it. Matching the
           file properly needs a shared StatBox change; until then this line is the only thing
           naming the feature on the tile. Do not remove it for consistency. PLA-8815. */
        value: WALLET_FEATURE_ADOPTION[0].pct,
        format: 'percent',
        denominator: `${WALLET_FEATURE_ADOPTION[0].label} leads adoption`,
        delta: { value: 5.4, unit: 'pp', direction: 'down', sentiment: 'negative' }
      }
    ],
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Wallet balances read from the ledger at period close',
        'Every funding source reports through the same rail'
      ]
    },
    whyThisResult:
      'QR and P2P activity grew, adding 1,506 distinct active users compared with the previous period.',
    evidenceNote:
      'Top-up intervals became more consistent and recent activity improved; amount stability remains the weakest component.'
  },
  payment_usage_risk: {
    cardKey: 'payment_usage_risk',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'payment_diversity_index',
        label: 'Payment Diversity Index',
        value: 0.72,
        format: 'ratio',
        delta: { value: 1.8, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'diversity_cohort',
        label: 'Diversity Cohort',
        value: 48600,
        format: 'count',
        delta: {
          value: 3200,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 8.1, unit: 'pp' }
        }
      },
      {
        key: 'wallet_velocity_anomaly_rate',
        label: 'Wallet Velocity Anomaly Rate',
        value: 3.8,
        format: 'percent',
        delta: { value: 3.2, unit: 'pp', direction: 'up', sentiment: 'negative' }
      },
      {
        key: 'anomalous_wallet_users',
        label: 'Anomalous Wallet Users',
        // The severity bands below sum to exactly this, so the count is taken from them rather than
        // stated twice: 1,034 low + 573 medium + 240 high.
        value: totalOfCounts(ANOMALY_SEVERITY_BANDS),
        format: 'count',
        delta: {
          value: 250,
          unit: 'count',
          direction: 'down',
          sentiment: 'positive',
          secondary: { value: 15.4, unit: 'pp' }
        }
      }
    ],
    // No `evidence` panel, for the reason `policy_health_claims` records: `2570:4672` draws ANOMALY
    // SIGNAL EVIDENCE — four chips over three reading rows, which the section now draws — and not a
    // rows-and-shares block. The flagged/cleared/queued split shipped here was composed before the
    // band had a mockup and describes a review queue the card never reports.
    confidence: {
      score: 83,
      band: 'high',
      reasons: [
        'Failure reasons reported by every funding source',
        'Review outcomes lag the flag by up to 48 hours'
      ]
    },
    whyThisResult:
      'QR and merchant-payment use expanded across more categories, while no single payment type exceeded 41% of activity.',
    evidenceNote:
      'Repeat-device activity normalized and high-frequency transaction spikes fell 18% versus the previous period.'
  }
};

// The `EVIDENCE INPUTS` chip rows, transcribed verbatim from the screen's own chips — `589-28148`
// (Debt & Spending Health) and `589-28230` (Income & Cashflow Health). The two spellings of the
// merchant chip are the file's, one per card, and are deliberately NOT unified here
// (`design/section-industry-devmode-2026-08-12.md` § 6, designer question Q4).
export const industryEvidenceInputs: Record<string, string[]> = {
  income_cashflow_health: [
    'transaction_event',
    'merchant/category_event',
    'account_event',
    'income_detection_feature',
    'account_balance_event',
    'obligation_event',
    'recurring_payment_event',
    'forecast_model_output'
  ],
  debt_spending_health: [
    'transaction_event',
    'merchant_category_event',
    'taxonomy_config',
    'loan_event',
    'repayment_event',
    'recurring_payment_event',
    'arrears_event',
    'payment_failure_event'
  ],
  /* PLA-8815 — the seven the three new bands draw, which had no entry at all. That is why every one
     of those cards rendered NO chip row: the section asks for `industryEvidenceInputs[card.key]`,
     this map held only the two Banking cards, and `EvidenceChipRow` given `undefined` draws nothing
     rather than failing. One absence, seven silent cards.

     Transcribed from `2533:1670`'s own `EVIDENCE INPUTS` rows. The spellings are the file's and are
     not unified: `wallet/payment_event` carries a solidus where its neighbours carry an underscore,
     the same divergence the two Banking cards already ship one each of. */
  churn_service_experience: [
    'network_quality_event',
    'complaint_event',
    'support_event',
    'sentiment_event',
    'churn_risk_event',
    'usage_event',
    'recharge_event',
    'billing_event',
    'churn_model_score'
  ],
  plan_usage_revenue: [
    'billing_event',
    'recharge_event',
    'subscription_event',
    'payment_event',
    'usage_event',
    'plan_event'
  ],
  recharge_upgrade_readiness: [
    'recharge_event',
    'wallet/payment_event',
    'plan_event',
    'usage_event',
    'billing_event',
    'complaint_event',
    'network_experience_event',
    'propensity_score_event'
  ],
  policy_health_claims: [
    'claim_event',
    'policy_event',
    'premium_event',
    'policy_status_event',
    'policy_effective_date',
    'policy_expiry_date',
    'premium_payment_event',
    'lapse_event',
    'surrender_event',
    'tenant_policy_good_standing_config',
    'payment_failure_event'
  ],
  renewal_growth_opportunities: [
    'policy_event',
    'renewal_event',
    'premium_payment_event',
    'claim_event',
    'sentiment_event',
    'model_score_event',
    'customer_profile_event',
    'life_stage_feature',
    'product_taxonomy_config',
    'profile_event',
    'propensity_score_event',
    'guardrail_event'
  ],
  wallet_activity_funding: [
    'wallet_event',
    'payment_event',
    'topup_event',
    'transfer_event',
    'app_event',
    'wallet_balance_event',
    'wallet_feature_event',
    'product_config_event'
  ],
  payment_usage_risk: [
    'payment_event',
    'merchant_category_event',
    'channel_event',
    'wallet_transaction_event',
    'device_event',
    'risk_event',
    'fraud_rule_event'
  ]
};

// Distributions the industry cards draw below their metric rows, keyed by industry so one industry's
// numbers can be reworked without touching another's. Raw counts and amounts only — colour, share and
// formatting stay the section's job. Every distribution totals the metric its own card reports, so
// bar, ring and metric row are one statement rather than three readings that can disagree:
// `spendSplit` 58.7 / 41.3, dividing to 1.42, the same
// split `breakdownFixtures.essential_vs_discretionary` publishes · `churnRiskDrivers` 12,880
// · `planMix` 96,140 active customers · `rechargeCadence` 83,560 · `policyMix` 58,800 ·
// `renewalWindow` 10,860 · `fundingSources` 66,740 · `failureReasons` 12,200 of 406,700 attempts ·
// `riskSignals` 1,840.
export const industryVisuals = {
  banking: {
    // No income-stability distribution: the block that stood here drew three credit-readability
    // bands over a 140,000 base, and `589-28063` draws no such bar on either card. What the Income
    // & Cashflow Health card puts in that place is `bankingCashflowHealth` and
    // `bankingForecastShortfallRisk` above.
    spendSplit: [
      { label: 'Essential', value: ESSENTIAL_SPEND },
      { label: 'Discretionary', value: DISCRETIONARY_SPEND }
    ],
    categorizedCustomers: CATEGORIZED_CUSTOMERS
  },
  /* PLA-8815: the three bands below now draw what `2533:1670` draws. What stood here was composed
     when the file had no mockup for them, and it no longer describes the same cards — the panels
     were a plan mix, a recharge cadence, a policy mix, a funding-source list and a payment-failure
     list, none of which any of the three bands draws, and none of which the re-pointed metric tiles
     above report. Keeping them would have left every card stating one quantity in its tiles and a
     different one in the panel beneath.

     Severity distributions are ordered low -> high, as the design draws them, so the section can
     tone them by position without a per-label lookup. Every one of them totals the population its
     own heading names. */
  telco: {
    // SERVICE-EXPERIENCE RISK · 1.24M ELIGIBLE SUBSCRIBERS
    serviceRiskBands: [
      { label: 'Low <40', value: 719000 },
      { label: 'Medium 40–69', value: 312000 },
      { label: 'High ≥70', value: SERVICE_RISK_HIGH }
    ],
    serviceRiskEvidence: [
      { label: 'Network quality', value: 34 },
      { label: 'Support interaction', value: 25 },
      { label: 'Complaints', value: 20 },
      { label: 'Negative sentiment', value: 14 }
    ],
    // TELCO CHURN-RISK DISTRIBUTION · 1.15M ACTIVE SUBSCRIBERS
    churnRiskBands: [
      { label: 'Low <40', value: 701000 },
      { label: 'Medium 40–69', value: 306400 },
      { label: 'High ≥70', value: CHURN_RISK_HIGH }
    ],
    churnRiskDrivers: [
      { label: 'Usage decline', value: 29 },
      { label: 'Recharge gap', value: 22 },
      { label: 'Network risk', value: 18 },
      { label: 'Complaints / sentiment', value: 14 }
    ],
    // DATA-UTILIZATION BANDS, over the same 1.15M the ARPU panel divides by.
    dataUtilisationBands: [
      { label: '<30%', value: 207000 },
      { label: '30-69%', value: 391000 },
      { label: '70-99%', value: 380000 },
      { label: '≥100%', value: 173000 }
    ],
    upgradeReadinessBands: UPGRADE_READINESS_BANDS,
    /* ARPU MOVEMENT · DAILY TREND is a PANEL line chart on `2558:3932`, not a tile ornament — it
       shipped as a tile sparkline in the panel's own unit (millions against a raw 32,660,000) and
       moved here with the panel that reads it. PLA-8815. */
    arpuTrend: TELCO_REVENUE_TREND,
    // The five rows ARPU MOVEMENT prints under its chart. The two quotients it also prints — the
    // current ARPU and the movement — are divided out of these in the section, so the panel cannot
    // stand a division beside figures that do not produce it.
    arpu: {
      revenue: TELCO_BILLABLE_REVENUE,
      subscribers: TELCO_ACTIVE_SUBSCRIBERS,
      baselineArpu: TELCO_BASELINE_ARPU
    },
    // ALLOWANCE UTILIZATION DISTRIBUTION states each allowance as used-over-included; both rates the
    // tiles print are those divisions.
    allowance: {
      dataUsedPb: TELCO_DATA_USED_PB,
      dataIncludedPb: TELCO_DATA_INCLUDED_PB,
      voiceUsedMinutes: TELCO_VOICE_USED_MIN,
      voiceIncludedMinutes: TELCO_VOICE_INCLUDED_MIN
    },
    // The populations the two Churn panel headings name, and the rows each closes on.
    serviceRiskEligible: SERVICE_RISK_ELIGIBLE,
    networkEventFreshnessMinutes: NETWORK_EVENT_FRESHNESS_MINUTES,
    churnActiveSubscribers: CHURN_ACTIVE_SUBSCRIBERS,
    churnPreviousRate: CHURN_RISK_PREVIOUS_RATE,
    churnHighRiskMovement: CHURN_HIGH_RISK_MOVEMENT,
    // The composite ring on Recharge & Upgrade Readiness, and the three rows beside it.
    rechargeRegularity: {
      score: RECHARGE_REGULARITY_SCORE,
      previousScore: RECHARGE_PREVIOUS_SCORE,
      components: RECHARGE_COMPONENTS,
      medianPerMonth: RECHARGE_MEDIAN_PER_MONTH,
      medianAmount: RECHARGE_MEDIAN_AMOUNT
    },
    upgradeReadinessEvidence: [
      { label: 'Utilization pressure', value: 31 },
      { label: 'Spend / add-ons', value: 23 },
      { label: 'Service quality', value: 17 },
      { label: 'Risk guardrails', value: 12 }
    ]
  },
  insurance: {
    policyStatus: POLICY_STATUS_COUNTS,
    // The POLICY ACTIVE RATE ring and the four rows beside it. `eligible` is the status counts' own
    // sum, so the ring, the bar under it and the tile above all divide by one population.
    policyActive: {
      active: POLICIES_ACTIVE,
      eligible: totalOfCounts(POLICY_STATUS_COUNTS),
      previousRate: POLICY_ACTIVE_PREVIOUS_RATE
    },
    premiumHealth: { score: 82, components: PREMIUM_HEALTH_COMPONENTS },
    // CLAIMS FREQUENCY & RATIO. Both quotients the panel prints are taken in the section from these
    // four figures rather than restated.
    claims: {
      count: CLAIM_COUNT,
      activePolicies: POLICIES_ACTIVE,
      claimAmount: CLAIM_AMOUNT,
      premiumAmount: PREMIUM_AMOUNT
    },
    renewalDuePolicies: RENEWAL_DUE_POLICIES,
    renewalHighRisk: RENEWAL_RISK_HIGH,
    renewalPreviousRate: RENEWAL_PREVIOUS_RATE,
    renewalRiskBands: RENEWAL_RISK_BANDS,
    renewalRiskDrivers: [
      { label: 'Premium health', value: 31 },
      { label: 'Engagement decline', value: 24 },
      { label: 'Claims / complaints', value: 18 },
      { label: 'Negative sentiment', value: 13 }
    ],
    coverageGapSignals: [
      { label: 'Protection amount gap', value: 34 },
      { label: 'Life-stage change', value: 24 },
      { label: 'Asset / loan exposure', value: 19 },
      { label: 'Claim/service evidence', value: 13 }
    ],
    riderReadinessBands: RIDER_READINESS_BANDS
  },
  fintech: {
    anomalySeverity: ANOMALY_SEVERITY_BANDS,
    // The PAYMENT DIVERSITY INDEX ring, whose centre is the tile's own 0.72 and whose legend is the
    // four entropy components the panel lists.
    diversityIndex: { score: 0.72, components: DIVERSITY_COMPONENTS },
    anomalySignals: ANOMALY_SIGNALS,
    // The three rows ANOMALY SIGNAL EVIDENCE closes on. The two shares total 100 — every anomaly is
    // raised by exactly one of the two routes.
    anomalySource: { ruleTriggered: 61, statistical: 39, latencyMinutes: 11 },
    // WALLET ACTIVE USER RATE · WEEKLY TREND: the five rows under the chart. Both rates the panel
    // prints divide out of these — 33,242/48,600 = 68.4% and 31,736/48,600 = 65.3%.
    walletActive: {
      active: WALLET_ACTIVE_USERS,
      eligible: WALLET_ELIGIBLE_USERS,
      previousActive: WALLET_PREVIOUS_ACTIVE_USERS
    },
    topupScore: {
      score: 74,
      components: TOPUP_COMPONENTS,
      medianPerMonth: WALLET_MEDIAN_TOPUPS_PER_MONTH,
      medianAmount: WALLET_MEDIAN_TOPUP_AMOUNT
    },
    // USE DISTRIBUTION CONTRIBUTING TO DIVERSITY — shares, and they total 100.
    diversityUseDistribution: [
      { label: 'Merchant payments', value: 41 },
      { label: 'P2P / transfers', value: 27 },
      { label: 'QR / scan-pay', value: 18 },
      { label: 'Bills / other', value: 14 }
    ],
    featureAdoption: WALLET_FEATURE_ADOPTION.map(({ label, pct }) => ({ label, value: pct })),
    // As `telco.arpuTrend` above: the weekly active-user RATE the left panel plots, waiting on the
    // panel's heading.
    walletActiveRateTrend: WALLET_ACTIVE_TREND
  }
};
